import { Link } from "react-router-dom";
import {
  Frame,
  Glass,
  GlassContainer,
  Html,
  LiquidCanvas,
  ZStack,
} from "@liquid-dom/react";
import { DEFAULT_LIGHT_DIRECTION, useLiquidGlassLight, useLiquidGlassSupport } from "../hooks/useLiquidGlass";

export default function LiquidGlassButton({
  children,
  to,
  href,
  width = 180,
  height = 52,
  tone = "dark",
  className = "",
  fallbackClassName = "btn btn-white",
  ariaLabel,
  target,
  rel,
  onClick,
  type = "button",
  form,
}) {
  const htmlInCanvasEnabled = useLiquidGlassSupport();
  const { glassContainerRef, onPointerMove, onPointerLeave } = useLiquidGlassLight();

  const commonProps = {
    className: `liquid-dom-button-content ${className}`.trim(),
    "aria-label": ariaLabel,
    onClick,
  };

  const renderControl = (liquid = false) => {
    const controlClassName = liquid
      ? commonProps.className
      : `${fallbackClassName} ${className}`.trim();
    const props = { ...commonProps, className: controlClassName };

    if (to) return <Link to={to} {...props}>{children}</Link>;
    if (href) {
      return <a href={href} target={target} rel={rel} {...props}>{children}</a>;
    }
    return <button type={type} form={form} {...props}>{children}</button>;
  };

  if (!htmlInCanvasEnabled) return renderControl(false);

  return (
    <span
      className={`liquid-dom-button liquid-dom-button-${tone}`}
      style={{ width, height }}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      <LiquidCanvas
        className="liquid-dom-button-canvas"
        canvasClassName="liquid-dom-button-canvas-element"
        maxDpr={2}
        frameloop="demand"
      >
        <ZStack alignment="center">
          <Html zIndex={-2} sizing="fill">
            <div className={`liquid-dom-button-backdrop liquid-dom-button-backdrop-${tone}`} />
          </Html>
          <GlassContainer
            ref={glassContainerRef}
            lightDirection={DEFAULT_LIGHT_DIRECTION}
            blur={10}
            bezelWidth={Math.max(16, height * 0.42)}
            thickness={82}
            tint={tone === "light"
              ? { r: 1, g: 1, b: 1, a: 0.18 }
              : { r: 0.75, g: 0.82, b: 1, a: 0.12 }}
            shadowColor={{ r: 0, g: 0, b: 0, a: tone === "light" ? 0.16 : 0.28 }}
            shadowOffsetY={6}
            shadowBlur={16}
            specularOpacity={0.72}
            specularWidth={1}
            specularStrength={1.1}
            specularSharpness={3}
            oppositeSpecularStrength={0.45}
          >
            <Glass cornerRadius={height / 2} cornerSmoothing={0.7}>
              <Frame width={width} height={height}>
                <Html sizing="fill">{renderControl(true)}</Html>
              </Frame>
            </Glass>
          </GlassContainer>
        </ZStack>
      </LiquidCanvas>
    </span>
  );
}
